/**
 * ImportPharmaciesModal Component
 * 
 * Modal for importing pharmacies from other clinics the user has access to
 * Allows bulk selection and saves to draft store
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge
} from './ui'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'

export default function ImportPharmaciesModal({ 
  isOpen, 
  onClose, 
  currentClinicId,
  onImport 
}) {
  const { user, getAvailableClinics } = useAuthStore()
  const [availableClinics, setAvailableClinics] = useState([])
  const [selectedClinicId, setSelectedClinicId] = useState('')
  const [pharmacies, setPharmacies] = useState([])
  const [selectedPharmacies, setSelectedPharmacies] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState(null)
  
  // Load available clinics when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadAvailableClinics()
    }
  }, [isOpen, user])
  
  // Load pharmacies when clinic is selected
  useEffect(() => {
    if (selectedClinicId) {
      loadPharmacies(selectedClinicId)
    }
  }, [selectedClinicId])
  
  // Initialize draft store
  useEffect(() => {
    if (isOpen && user && currentClinicId) {
      const { initialize, isInitialized, clinicId: storeClinicId, userId: storeUserId } = useDraftStore.getState()
      const userId = user.id
      
      if (!isInitialized || storeUserId !== userId || storeClinicId !== currentClinicId) {
        console.log('[IMPORT MODAL] Initializing draft store with:', { clinicId: currentClinicId, userId })
        initialize(currentClinicId, userId)
      }
    }
  }, [isOpen, user, currentClinicId])
  
  const loadAvailableClinics = () => {
    try {
      // Get all clinics the user has access to from auth store
      const allClinics = getAvailableClinics()
      
      // Filter out current clinic
      const filtered = allClinics.filter(c => c.id !== currentClinicId)
      setAvailableClinics(filtered)
      
      console.log('[IMPORT MODAL] Available clinics for import:', filtered)
    } catch (error) {
      console.error('[IMPORT] Error loading clinics:', error)
      setAvailableClinics([])
    }
  }
  
  const loadPharmacies = async (clinicId) => {
    try {
      setLoading(true)
      setError(null)
      
      // Use source_clinic query parameter to fetch from a different clinic
      const response = await api.get('/pharmacy', {
        params: { 
          source_clinic: clinicId,  // Tell backend which clinic to fetch from
          limit: 1000, 
          include_disabled: false 
        }
      })
      
      setPharmacies(response.data?.data || response.data?.pharmacies || [])
      setSelectedPharmacies(new Set())
    } catch (error) {
      console.error('[IMPORT] Error loading pharmacies:', error)
      const errorMsg = error.response?.status === 404 
        ? `Clinic "${clinicId}" not found or has no pharmacy data available.`
        : error.response?.status === 403
        ? `You don't have permission to access clinic "${clinicId}".`
        : error.response?.data?.message || 'Failed to load pharmacies from selected clinic.'
      setError(errorMsg)
      setPharmacies([])
    } finally {
      setLoading(false)
    }
  }
  
  const togglePharmacy = (pharmacyId) => {
    setSelectedPharmacies(prev => {
      const newSet = new Set(prev)
      if (newSet.has(pharmacyId)) {
        newSet.delete(pharmacyId)
      } else {
        newSet.add(pharmacyId)
      }
      return newSet
    })
  }
  
  const selectAll = () => {
    setSelectedPharmacies(new Set(pharmacies.map(p => p._id)))
  }
  
  const deselectAll = () => {
    setSelectedPharmacies(new Set())
  }
  
  const handleImport = async () => {
    if (selectedPharmacies.size === 0) {
      alert('Please select at least one pharmacy to import')
      return
    }
    
    try {
      setImporting(true)
      const { addChange } = useDraftStore.getState()
      
      // Get selected pharmacy objects
      const selectedPharmacyObjects = pharmacies.filter(p => selectedPharmacies.has(p._id))
      
      // Add each pharmacy as a create change to the draft store
      for (const pharmacy of selectedPharmacyObjects) {
        // Remove _id and clinic-specific fields for import
        const { _id, created_at, updated_at, created_by, updated_by, ...pharmacyData } = pharmacy
        
        addChange({
          resource_type: 'pharmacy',
          resource_id: pharmacy.pharmacy_ncpdpid || `import_${Date.now()}_${Math.random()}`,
          operation: 'create',
          original_data: null,
          new_data: pharmacyData,
          description: `Import pharmacy: ${pharmacy.pharmacy_name} from ${selectedClinicId}`
        })
      }
      
      console.log(`[IMPORT] Added ${selectedPharmacies.size} pharmacies to drafts`)
      
      // Notify parent and close
      onImport?.()
      onClose()
      alert(`Successfully added ${selectedPharmacies.size} pharmacies to drafts. Publish a release to apply the changes.`)
    } catch (error) {
      console.error('[IMPORT] Error importing pharmacies:', error)
      alert('Failed to import pharmacies. Please try again.')
    } finally {
      setImporting(false)
    }
  }
  
  const handleClose = () => {
    setSelectedClinicId('')
    setPharmacies([])
    setSelectedPharmacies(new Set())
    setError(null)
    onClose()
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Pharmacies from Another Clinic</DialogTitle>
          <DialogDescription>
            Select a clinic to view their pharmacies, then choose which ones to import.
            Changes will be saved as drafts.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Clinic Selector */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Select Source Clinic
              {availableClinics.length === 0 && (
                <span className="text-muted-foreground font-normal ml-2">
                  (No other clinics available)
                </span>
              )}
            </label>
            <Select 
              value={selectedClinicId} 
              onValueChange={setSelectedClinicId}
              disabled={availableClinics.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={availableClinics.length === 0 ? "No clinics available" : "Choose a clinic..."} />
              </SelectTrigger>
              <SelectContent>
                {availableClinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name || clinic.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableClinics.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                You don't have access to any other clinics, or you're already viewing all available clinics.
              </p>
            )}
          </div>
          
          {/* Selection Controls */}
          {pharmacies.length > 0 && (
            <div className="flex items-center justify-between border-b pb-2">
              <div className="text-sm text-muted-foreground">
                {selectedPharmacies.size} of {pharmacies.length} selected
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  Select All
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAll}>
                  Deselect All
                </Button>
              </div>
            </div>
          )}
          
          {/* Pharmacies List */}
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {loading && (
              <div className="p-8 text-center text-muted-foreground">
                Loading pharmacies...
              </div>
            )}
            
            {error && (
              <div className="p-8 text-center">
                <div className="text-destructive font-medium mb-2">Error Loading Pharmacies</div>
                <div className="text-sm text-muted-foreground">{error}</div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => loadPharmacies(selectedClinicId)}
                >
                  Try Again
                </Button>
              </div>
            )}
            
            {!loading && !error && !selectedClinicId && (
              <div className="p-8 text-center text-muted-foreground">
                Select a clinic to view available pharmacies
              </div>
            )}
            
            {!loading && !error && selectedClinicId && pharmacies.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                No pharmacies found in selected clinic
              </div>
            )}
            
            {!loading && !error && pharmacies.length > 0 && (
              <div className="divide-y">
                {pharmacies.map(pharmacy => (
                  <label
                    key={pharmacy._id}
                    className="flex items-start gap-3 p-4 hover:bg-muted/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPharmacies.has(pharmacy._id)}
                      onChange={() => togglePharmacy(pharmacy._id)}
                      className="mt-1 w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{pharmacy.pharmacy_name}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        <div>NCPDPID: {pharmacy.pharmacy_ncpdpid}</div>
                        {pharmacy.pharmacy_address && (
                          <div>
                            {pharmacy.pharmacy_address.line}, {pharmacy.pharmacy_address.city}, {pharmacy.pharmacy_address.state} {pharmacy.pharmacy.address?.zip}
                          </div>
                        )}
                        {pharmacy.states && pharmacy.states.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {pharmacy.states.map(state => (
                              <Badge key={state} variant="secondary" className="text-xs">
                                {state}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={importing}>
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={importing || selectedPharmacies.size === 0}
          >
            {importing ? 'Importing...' : `Import ${selectedPharmacies.size} Pharmacies to Drafts`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

